import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardHeader, 
  Grid, 
  TextField, 
  MenuItem, 
  Button, 
  Divider,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  IconButton,
  Tooltip,
  useTheme,
  Paper,
  Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

const ActivityDataInputPage = () => {
  const theme = useTheme();
  const [activityType, setActivityType] = useState('');
  const [activityDate, setActivityDate] = useState(new Date());
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState('');
  const [description, setDescription] = useState('');
  const [region, setRegion] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // 활동 유형에 따른 단위 옵션
  const unitOptions = {
    transportation: ['km', 'miles', 'hours'],
    electricity: ['kWh', 'MWh'],
    heating: ['m³', 'kWh', 'liters'],
    food: ['kg', 'servings'],
    other: ['kg', 'units', 'hours']
  };

  // 활동 유형에 따른 아이콘
  const activityIcons = {
    transportation: <DirectionsCarIcon sx={{ fontSize: 40, color: theme.palette.chart.c2 }} />,
    electricity: <ElectricBoltIcon sx={{ fontSize: 40, color: theme.palette.chart.c3 }} />,
    heating: <LocalFireDepartmentIcon sx={{ fontSize: 40, color: theme.palette.chart.c4 }} />,
    food: <RestaurantIcon sx={{ fontSize: 40, color: theme.palette.chart.c5 }} />,
    other: <MoreHorizIcon sx={{ fontSize: 40, color: theme.palette.text.secondary }} />
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log({
      activityType,
      activityDate,
      quantity,
      unit,
      description,
      region
    });
    
    // 성공 메시지 표시
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
    }, 3000);
    
    // 폼 초기화
    setQuantity('');
    setDescription('');
  };

  const handleReset = () => {
    setActivityType('');
    setActivityDate(new Date());
    setQuantity('');
    setUnit('');
    setDescription('');
    setRegion('');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ flexGrow: 1, py: 0 }}>
        {/* <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
          활동 데이터 입력
        </Typography> */}
        
        {showSuccess && (
          <Alert 
            severity="success" 
            sx={{ mb: 3 }}
            onClose={() => setShowSuccess(false)}
          >
            활동 데이터가 성공적으로 저장되었습니다.
          </Alert>
        )}
        
        <Grid container spacing={3}>
          {/* 활동 데이터 입력 폼 */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardHeader 
                title="새 활동 데이터 추가" 
                subheader="탄소 발자국 계산을 위한 활동 정보를 입력하세요"
              />
              <Divider />
              <CardContent>
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel id="activity-type-label">활동 유형</InputLabel>
                        <Select
                          labelId="activity-type-label"
                          id="activity-type"
                          value={activityType}
                          label="활동 유형"
                          onChange={(e) => {
                            setActivityType(e.target.value);
                            setUnit(''); // 활동 유형이 변경되면 단위 초기화
                          }}
                        >
                          <MenuItem value="transportation">교통</MenuItem>
                          <MenuItem value="electricity">전기</MenuItem>
                          <MenuItem value="heating">난방</MenuItem>
                          <MenuItem value="food">식품</MenuItem>
                          <MenuItem value="other">기타</MenuItem>
                        </Select>
                        <FormHelperText>탄소 배출과 관련된 활동 유형을 선택하세요</FormHelperText>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label="활동 날짜"
                        value={activityDate}
                        onChange={(newValue) => setActivityDate(newValue)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            required: true,
                            helperText: '활동이 발생한 날짜를 선택하세요'
                          }
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        id="quantity"
                        label="수량"
                        type="number"
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        InputProps={{
                          inputProps: { min: 0, step: 0.01 }
                        }}
                        helperText="활동의 수량을 입력하세요 (예: 주행 거리, 전력 사용량 등)"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required disabled={!activityType}>
                        <InputLabel id="unit-label">단위</InputLabel>
                        <Select
                          labelId="unit-label"
                          id="unit"
                          value={unit}
                          label="단위"
                          onChange={(e) => setUnit(e.target.value)}
                        >
                          {activityType && unitOptions[activityType].map((option) => (
                            <MenuItem key={option} value={option}>{option}</MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>활동 수량의 측정 단위를 선택하세요</FormHelperText>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel id="region-label">지역</InputLabel>
                        <Select
                          labelId="region-label"
                          id="region"
                          value={region}
                          label="지역"
                          onChange={(e) => setRegion(e.target.value)}
                        >
                          <MenuItem value="seoul">서울</MenuItem>
                          <MenuItem value="busan">부산</MenuItem>
                          <MenuItem value="incheon">인천</MenuItem>
                          <MenuItem value="daegu">대구</MenuItem>
                          <MenuItem value="gwangju">광주</MenuItem>
                          <MenuItem value="daejeon">대전</MenuItem>
                          <MenuItem value="ulsan">울산</MenuItem>
                          <MenuItem value="gyeonggi">경기도</MenuItem>
                          <MenuItem value="other">기타</MenuItem>
                        </Select>
                        <FormHelperText>활동이 발생한 지역을 선택하세요 (선택사항)</FormHelperText>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        id="description"
                        label="설명"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        rows={1}
                        helperText="활동에 대한 추가 설명을 입력하세요 (선택사항)"
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                        <Button
                          variant="outlined"
                          startIcon={<ClearIcon />}
                          onClick={handleReset}
                        >
                          초기화
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={<SaveIcon />}
                          disabled={!activityType || !quantity || !unit}
                        >
                          저장
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* 활동 유형 정보 카드 */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardHeader 
                title="활동 유형 정보" 
                action={
                  <Tooltip title="활동 유형별 탄소 배출 계산 방법에 대한 정보입니다">
                    <IconButton>
                      <InfoOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                }
              />
              <Divider />
              <CardContent>
                {activityType ? (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Box sx={{ mb: 2 }}>
                      {activityIcons[activityType]}
                    </Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {activityType === 'transportation' && '교통'}
                      {activityType === 'electricity' && '전기'}
                      {activityType === 'heating' && '난방'}
                      {activityType === 'food' && '식품'}
                      {activityType === 'other' && '기타'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {activityType === 'transportation' && '자동차, 대중교통, 비행기 등 이동 수단 사용으로 인한 탄소 배출량을 계산합니다.'}
                      {activityType === 'electricity' && '가정 및 사무실에서 사용하는 전기 에너지로 인한 탄소 배출량을 계산합니다.'}
                      {activityType === 'heating' && '난방을 위한 가스, 석유 등 연료 사용으로 인한 탄소 배출량을 계산합니다.'}
                      {activityType === 'food' && '식품 생산, 운송, 소비 과정에서 발생하는 탄소 배출량을 계산합니다.'}
                      {activityType === 'other' && '기타 활동으로 인한 탄소 배출량을 계산합니다.'}
                    </Typography>
                    
                    <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'background.lighter' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        권장 단위:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                        {unitOptions[activityType].map((unit) => (
                          <Tooltip key={unit} title={`${unit} 단위로 입력`}>
                            <Chip 
                              label={unit} 
                              variant="outlined" 
                              sx={{ 
                                backgroundColor: 'background.paper',
                                borderColor: 'primary.main',
                                '& .MuiChip-label': {
                                  color: 'text.primary'
                                }
                              }} 
                            />
                          </Tooltip>
                        ))}
                      </Box>
                    </Paper>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      활동 유형을 선택하면 관련 정보가 표시됩니다.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          {/* 최근 입력 데이터 */}
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title="최근 입력 데이터" 
                action={
                  <Button variant="text" color="primary">
                    모두 보기
                  </Button>
                }
              />
              <Divider />
              <Box sx={{ overflow: 'auto' }}>
                <Box sx={{ minWidth: 800 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>활동 유형</TableCell>
                        <TableCell>날짜</TableCell>
                        <TableCell>수량</TableCell>
                        <TableCell>단위</TableCell>
                        <TableCell>지역</TableCell>
                        <TableCell>설명</TableCell>
                        <TableCell>탄소 배출량 (kg CO₂e)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>교통</TableCell>
                        <TableCell>2025-05-27</TableCell>
                        <TableCell>45</TableCell>
                        <TableCell>km</TableCell>
                        <TableCell>서울</TableCell>
                        <TableCell>출퇴근 자가용 이용</TableCell>
                        <TableCell>8.55</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>전기</TableCell>
                        <TableCell>2025-05-26</TableCell>
                        <TableCell>12.5</TableCell>
                        <TableCell>kWh</TableCell>
                        <TableCell>서울</TableCell>
                        <TableCell>가정용 전기 사용</TableCell>
                        <TableCell>5.75</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>난방</TableCell>
                        <TableCell>2025-05-25</TableCell>
                        <TableCell>3.2</TableCell>
                        <TableCell>m³</TableCell>
                        <TableCell>서울</TableCell>
                        <TableCell>가스 난방</TableCell>
                        <TableCell>7.10</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

// 테이블 컴포넌트
const Table = ({ children }) => (
  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
    {children}
  </table>
);

const TableHead = ({ children }) => (
  <thead style={{ backgroundColor: '#132F4C' }}>
    {children}
  </thead>
);

const TableBody = ({ children }) => (
  <tbody>
    {children}
  </tbody>
);

const TableRow = ({ children }) => (
  <tr style={{ '&:hover': { backgroundColor: '#173A5E' } }}>
    {children}
  </tr>
);

const TableCell = ({ children }) => (
  <td style={{ padding: '12px 16px', borderBottom: '1px solid #173A5E' }}>
    {children}
  </td>
);

// 칩 컴포넌트
const Chip = ({ label, variant, sx }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      height: '32px',
      borderRadius: '16px',
      padding: '0 12px',
      fontSize: '0.8125rem',
      backgroundColor: variant === 'outlined' ? 'transparent' : sx?.backgroundColor,
      border: variant === 'outlined' ? `1px solid ${sx?.borderColor || '#173A5E'}` : 'none',
      ...sx
    }}
  >
    <span style={{ color: sx?.['& .MuiChip-label']?.color || 'inherit' }}>
      {label}
    </span>
  </Box>
);

export default ActivityDataInputPage;
